import { ClipTranscriptSegment } from '@src/editor/models/ClipTranscriptSegment';
import { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import { splitTokenValueIntoTokens } from '@src/editor/services/transcriptTokenUtils';
import { LogManager } from '@src/utility/Logger';

import { AbstractCommand } from '../AbstractCommand';

import type {
  ClipTranscriptTextToken,
  ClipTranscriptToken,
} from '@src/editor/types';
import type { VideoEditor } from '@src/editor/VideoEditor';

const logger = LogManager.getLogger('transcript:multiRangeReplace');

export interface SegmentTextChange {
  fromIndex: number; // inclusive
  toIndex: number; // inclusive
  newText: string; // replacement text (may be empty -> removal)
}

/**
 * Multi‑range single segment text replacement.
 * Allows multiple disjoint, non‑overlapping ranges within one segment to be replaced in one command,
 * eliminating the need to recalculate segment IDs between sequential edits.
 * Validation: ranges must be sorted ascending and non‑overlapping relative to ORIGINAL segment indexes.
 * Undo: stores original segment (deep copy) and restores on undo.
 */
export class TranscriptMultiRangeTextReplaceCommand extends AbstractCommand {
  private readonly segmentId: string;
  private readonly changes: SegmentTextChange[];

  private previousSegment: ClipTranscriptSegment | null = null;
  private element: VideoTranscriptElement | null = null;

  constructor(
    editor: VideoEditor,
    segmentId: string,
    changes: SegmentTextChange[],
  ) {
    super(editor);
    this.segmentId = segmentId;
    // Normalize + sort and run basic validation early.
    this.changes = changes
      .map(c => ({
        ...c,
        fromIndex: Math.min(c.fromIndex, c.toIndex),
        toIndex: Math.max(c.fromIndex, c.toIndex),
      }))
      .sort((a, b) => a.fromIndex - b.fromIndex);
  }

  getName(): string {
    return 'TranscriptMultiRangeTextReplaceCommand';
  }
  getDescription(): string {
    return `Replace ${this.changes.length} ranges in segment ${this.segmentId}`;
  }

  execute(): boolean {
    const track = this.editor.getVideoTranscriptTrack();
    if (!track) {
      logger.error('No transcript track available');
      return false;
    }

    const el = track.transcriptSegmentElements.find(
      el => el.getClipTranscriptSegment().id === this.segmentId,
    );
    if (!(el instanceof VideoTranscriptElement)) {
      logger.error('Segment element not found or invalid', {
        segmentId: this.segmentId,
      });
      return false;
    }
    this.element = el;

    const originalSegment = el.getClipTranscriptSegment();
    const originalTokens = originalSegment.getWordsWithPunctuation();

    if (!this.validateRanges(originalTokens.length)) return false;

    // Deep copy for undo
    this.previousSegment = new ClipTranscriptSegment(
      [...originalTokens],
      originalSegment.mediaStart,
      originalSegment.mediaEnd,
    );

    const newSegment = this.createNewSegmentWithWordsReplaced(originalSegment);
    if (!newSegment) return false;

    el.setClipTranscriptSegment(newSegment);
    this.recalculateTimeline();
    this.editor.markDirty('transcript_multi_range_text_replace');
    return true;
  }

  undo(): boolean {
    if (!this.element || !this.previousSegment) return false;
    this.element.setClipTranscriptSegment(this.previousSegment);
    this.recalculateTimeline();
    this.editor.markDirty('transcript_multi_range_text_replace_undo');
    return true;
  }

  private validateRanges(tokenCount: number): boolean {
    for (let i = 0; i < this.changes.length; i++) {
      const c = this.changes[i];
      if (c.fromIndex < 0 || c.toIndex > tokenCount - 1) {
        logger.error('Range out of bounds', c);
        return false;
      }
      if (c.fromIndex > c.toIndex) {
        logger.error('Invalid range ordering', c);
        return false;
      }
      if (i > 0) {
        const prev = this.changes[i - 1];
        if (prev.toIndex >= c.fromIndex) {
          logger.error('Overlapping ranges not supported', {
            prev,
            current: c,
          });
          return false;
        }
      }
    }
    return true;
  }

  private createNewSegmentWithWordsReplaced(
    segment: ClipTranscriptSegment,
  ): ClipTranscriptSegment | false {
    const originalTokens = segment.getWordsWithPunctuation();
    let workingTokens = [...originalTokens];
    let offset = 0; // net index shift due to previous replacements

    for (const change of this.changes) {
      const { fromIndex, toIndex, newText } = change;
      const adjFrom = fromIndex + offset;
      const adjTo = toIndex + offset;
      const replacedSlice = workingTokens.slice(adjFrom, adjTo + 1);
      const replacementTokens = this.splitReplacementIntoTimedTokens(
        segment,
        replacedSlice,
        newText,
        adjFrom,
        adjTo,
      );
      workingTokens = [
        ...workingTokens.slice(0, adjFrom),
        ...replacementTokens,
        ...workingTokens.slice(adjTo + 1),
      ];
      offset += replacementTokens.length - replacedSlice.length;
    }

    return new ClipTranscriptSegment(
      workingTokens,
      segment.mediaStart,
      segment.mediaEnd,
    );
  }

  /**
   * Derive timing interval for a replacement slice similar to single-range command logic.
   */
  private splitReplacementIntoTimedTokens(
    segment: ClipTranscriptSegment,
    replacedSlice: ClipTranscriptToken[],
    newText: string,
    fromIndex: number,
    toIndex: number,
  ): ClipTranscriptToken[] {
    if (!newText.length) return []; // deletion
    const allTokens = segment.getWordsWithPunctuation();

    const isText = (t: ClipTranscriptToken): t is ClipTranscriptTextToken =>
      t.type === 'text' && 'relative_ts' in t && 'relative_end_ts' in t;

    const segmentDuration = segment.mediaEnd - segment.mediaStart;
    const textTokensInSlice = replacedSlice.filter(isText);

    // Extract context from the first replaced text token to preserve paragraph formatting
    const contextToken = textTokensInSlice[0];
    const context = contextToken
      ? {
          paragraph_id: contextToken.paragraph_id,
          speaker_id: contextToken.speaker_id,
          speaker_label: contextToken.speaker_label,
          segment_id: contextToken.segment_id,
        }
      : undefined;

    let intervalStart: number;
    let intervalEnd: number;
    if (textTokensInSlice.length) {
      intervalStart = textTokensInSlice[0].relative_ts;
      intervalEnd =
        textTokensInSlice[textTokensInSlice.length - 1].relative_end_ts;
    } else {
      // punctuation-only slice: use neighbors
      const prev = ClipTranscriptSegment.findPreviousTextWord(
        fromIndex,
        allTokens,
      );
      const next = ClipTranscriptSegment.findNextTextWord(toIndex, allTokens);
      intervalStart = prev && isText(prev) ? prev.relative_end_ts : 0;
      intervalEnd = next && isText(next) ? next.relative_ts : segmentDuration;
      if (intervalEnd < intervalStart) intervalEnd = intervalStart; // guard
    }
    const availableDuration = Math.max(0, intervalEnd - intervalStart);
    const mediaStart = segment.mediaStart + intervalStart;

    // Pass context to preserve paragraph_id and other properties, and the
    // replaced text tokens so each replacement word inherits the identity of
    // the word it replaces (PLAT-10266) instead of losing its translation link.
    return splitTokenValueIntoTokens(
      newText,
      mediaStart,
      intervalStart,
      availableDuration,
      -1,
      'added',
      context,
      textTokensInSlice,
    );
  }
}
